/**
 * New event instance (R2-01). Subsource picklist comes from LP's event
 * subsources; choosing one pre-fills the name and the first/last lead dates.
 * An overlapping window for the same subsource is refused by the API (R2-02)
 * with a message naming the existing event.
 */
import { AlertCircle, ArrowLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";

import { useAuth } from "../../auth/AuthContext";
import { EventsNav } from "../../events/EventsNav";
import { createRecap, fetchSubsources } from "../../lib/eventReporting";
import type { SubsourceOption } from "../../lib/eventReporting";

const inputCls = "px-3 py-1.5 rounded-md border hairline text-[13.5px] focus-ring w-full";
const inputStyle = { background: "var(--bg)", color: "var(--ink)" };

export function EventRecapNew() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [options, setOptions] = useState<SubsourceOption[]>([]);
  const [subsource, setSubsource] = useState("");
  const [name, setName] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [attrEnd, setAttrEnd] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSubsources().then(setOptions).catch((e: Error) => setError(e.message));
  }, []);

  const picked = useMemo(() => options.find((o) => o.subsource === subsource) ?? null, [options, subsource]);

  function pick(value: string) {
    setSubsource(value);
    const o = options.find((x) => x.subsource === value);
    if (o) {
      setName((n) => n || o.subsource);
      if (o.first_lead) setStart(o.first_lead);
      if (o.last_lead) setEnd(o.last_lead);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (end < start) return setError("The end date is before the start date.");
    if (attrEnd && attrEnd < end) return setError("Attribution end must be on or after the end date.");
    setSaving(true);
    try {
      const { instance_id } = await createRecap({
        subsource: subsource.trim(), display_name: name.trim(), start_date: start, end_date: end,
        attribution_end: attrEnd || null,
      });
      navigate(`/events/${instance_id}`);
    } catch (err) {
      setError((err as Error).message);
      setSaving(false);
    }
  }

  // Editors only; the API refuses the POST for anyone else anyway (403 + RLS).
  if (user && !user.has_event_editor) return <Navigate to="/events" replace />;

  return (
    <div className="p-8 view-in overflow-y-auto h-full scroll-quiet">
      <header className="mb-4">
        <Link to="/events" className="inline-flex items-center gap-1 text-[13px] mb-2 focus-ring rounded-sm" style={{ color: "var(--ink-3)" }}>
          <ArrowLeft size={13} /> Event Recaps
        </Link>
        <h1 className="font-serif text-[28px] leading-tight" style={{ color: "var(--ink)" }}>New event</h1>
        <p className="text-[14.5px] font-serif italic mt-1" style={{ color: "var(--ink-3)" }}>
          Leads, costs and hours fill in automatically from LeadPerfection at the next data refresh.
        </p>
      </header>

      <EventsNav />

      <form onSubmit={submit} className="rounded-lg border hairline px-5 py-5 max-w-2xl grid gap-4" style={{ background: "var(--bg-elev)" }}>
        <Field label="LeadPerfection subsource" hint={picked
          ? `${picked.leads} lead${picked.leads === 1 ? "" : "s"} since October 2023${picked.first_lead ? `, ${picked.first_lead} to ${picked.last_lead}` : ""}${picked.instances ? ` · already has ${picked.instances} recap${picked.instances === 1 ? "" : "s"}` : ""}`
          : "Must match the subsource on the event's leads exactly."}>
          <input list="event-subsources" required value={subsource} onChange={(e) => pick(e.target.value)}
            placeholder="Start typing an event name" className={inputCls} style={inputStyle} />
          <datalist id="event-subsources">
            {options.map((o) => <option key={o.subsource} value={o.subsource} />)}
          </datalist>
        </Field>
        <Field label="Display name">
          <input required maxLength={200} value={name} onChange={(e) => setName(e.target.value)} className={inputCls} style={inputStyle} />
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Start date"><input type="date" required value={start} onChange={(e) => setStart(e.target.value)} className={inputCls} style={inputStyle} /></Field>
          <Field label="End date"><input type="date" required value={end} onChange={(e) => setEnd(e.target.value)} className={inputCls} style={inputStyle} /></Field>
          <Field label="Attribution end (optional)" hint="Only matters when a subsource has several events.">
            <input type="date" value={attrEnd} onChange={(e) => setAttrEnd(e.target.value)} className={inputCls} style={inputStyle} />
          </Field>
        </div>
        {error && (
          <div className="rounded-md p-3 text-[13px] flex items-start gap-2"
            style={{ background: "var(--oxblood-bg)", color: "var(--oxblood)", border: "1px solid var(--oxblood)" }}>
            <AlertCircle size={14} className="mt-0.5 shrink-0" /> <span>{error}</span>
          </div>
        )}
        <div>
          <button type="submit" disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md font-serif text-[14px] t-base focus-ring disabled:opacity-50"
            style={{ background: "var(--ink)", color: "var(--bg)" }}>
            {saving ? "Creating…" : "Create event"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ink-3)" }}>{label}</div>
      {children}
      {hint && <div className="text-[12px] mt-1" style={{ color: "var(--ink-3)" }}>{hint}</div>}
    </label>
  );
}
